import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { EnvService } from '@services/env.service';
import {
    AuthService,
    ReplacedImpersonationSessionError,
} from '@services/auth.service';
import { BroadcastService } from '@services/broadcast.service';
import {
    buildApiFailureReportContext,
    reportApiFailure,
    shouldReportApiFailure,
} from '@services/trackjs/trackjs.config';
import { TRACKJS_CLIENT, TrackjsClient } from '@services/trackjs/trackjs-client';

export class TokenRefreshError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'TokenRefreshError';
    }
}

type RefreshSignal = { token: string; error: boolean } | null;

const loginPaths: readonly RegExp[] = [
    /\/users\/refresh-login/,
    /\/users\/refresh/,
    /\/users\/google-oauth/,
    /\/users\/reset-password/,
    /\/users\/password-reset/,
    /\/users\/password-reset-legacy/,
    /\/users\/validate-invitation-email/,
    /\/users\/logout-session/,
];

/**
 * Singleton state shared across every invocation of jwtInterceptor.
 * Holds the in-flight refresh BehaviorSubject and listens for refresh
 * completion signals from this tab (AuthService.tokenRefreshed$) and
 * from other tabs (BroadcastService).
 */
@Injectable({ providedIn: 'root' })
export class JwtInterceptorState {
    private auth = inject(AuthService);
    private broadcastService = inject(BroadcastService);

    refreshTokenSubject = new BehaviorSubject<RefreshSignal>(null);

    constructor() {
        this.broadcastService.onMessage$.subscribe(message => {
            if (message.type !== 'refresh-user' || !this.auth.isRefreshing) {
                return;
            }

            if (message.data.status === 'complete') {
                const token = this.auth.accessToken ?? '';
                this.publishAndReset({ token, error: false });
            } else if (message.data.status === 'error') {
                this.publishAndReset({ token: '', error: true });
            }
        });

        this.auth.tokenRefreshed$.subscribe(() => {
            this.publishAndReset({ token: this.auth.accessToken, error: false });
        });
    }

    private publishAndReset(signal: RefreshSignal): void {
        this.refreshTokenSubject.next(signal);
        this.refreshTokenSubject.complete();
        this.refreshTokenSubject = new BehaviorSubject<RefreshSignal>(null);
    }
}

function addLanguageHeader(request: HttpRequest<unknown>, language: string): HttpRequest<unknown> {
    if (request.headers.has('x-ppm-language')) {
        return request;
    }

    return request.clone({
        setHeaders: {
            'x-ppm-language': language,
        },
    });
}

function addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    let headers: Record<string, string> = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
    };

    if (!(request.body instanceof FormData) && !request.headers.has('Content-Type')) {
        headers = { ...headers, 'Content-Type': 'application/json; charset=utf-8' };
    }

    return request.clone({ setHeaders: headers });
}

function handleSessionExpired(
    auth: AuthService,
    router: Router,
    state: JwtInterceptorState,
): Observable<never> {
    auth.isRefreshing = false;
    auth.clearSession();
    state.refreshTokenSubject.next({ token: '', error: true });
    state.refreshTokenSubject.complete();
    state.refreshTokenSubject = new BehaviorSubject<RefreshSignal>(null);
    void router.navigate(['/login'], {
        queryParams: { returnUrl: router.url },
    });
    return throwError(() => new TokenRefreshError('Token refresh failed'));
}

function waitForRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    auth: AuthService,
    router: Router,
    state: JwtInterceptorState,
): Observable<HttpEvent<unknown>> {
    return state.refreshTokenSubject.pipe(
        switchMap(token => {
            if (token && !token.error) {
                return next(addTokenHeader(request, token.token));
            }
            if (token && token.error) {
               throw new Error('Primary refresh request failed')
            }

            if (auth.isRefreshing) {
            return waitForRefresh(request, next, auth, router, state);
            }
            else {
               return throwError(() => new TokenRefreshError('Refresh timeout'));
            }
        }),
    );
}

function reportApiFailureIfNeeded(
    trackjs: TrackjsClient,
    request: HttpRequest<unknown>,
    router: Router,
    error: unknown,
): void {
    if (error instanceof HttpErrorResponse && shouldReportApiFailure(error.status)) {
        reportApiFailure(
            trackjs,
            buildApiFailureReportContext(request.url, error.status, router.url),
        );
    }
}

function handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    auth: AuthService,
    router: Router,
    state: JwtInterceptorState,
): Observable<HttpEvent<unknown>> {
    if (auth.isRefreshing) {
        return waitForRefresh(request, next, auth, router, state);
    }

    auth.isRefreshing = true;
    state.refreshTokenSubject.next(null);

    return auth.refreshLogin().pipe(
        switchMap(token => {
            auth.isRefreshing = false;
            return next(addTokenHeader(request, token.accessToken));
        }),
        catchError((error) => {
            if (error instanceof ReplacedImpersonationSessionError) {
                auth.isRefreshing = false;
                state.refreshTokenSubject.next({ token: '', error: true });
                state.refreshTokenSubject.complete();
                state.refreshTokenSubject = new BehaviorSubject<RefreshSignal>(null);
                return throwError(() => error);
            }

            return handleSessionExpired(auth, router, state);
        }),
    );
}

export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
    const envService = inject(EnvService);
    const auth = inject(AuthService);
    const router = inject(Router);
    const state = inject(JwtInterceptorState);
    const transloco = inject(TranslocoService);
    const trackjs = inject(TRACKJS_CLIENT);

    const language = transloco.getActiveLang();
    const matchesApi = !!request.url.match(`${envService.apiUrl}`);
    const isLoginPath = loginPaths.some(path => request.url.match(path));

    if (!matchesApi) {
        return next(request);
    }

    const requestWithLanguage = addLanguageHeader(request, language);

    if (isLoginPath) {
        return next(requestWithLanguage).pipe(
            catchError(error => {
                reportApiFailureIfNeeded(trackjs, requestWithLanguage, router, error);
                return throwError(() => error);
            }),
        );
    }

    const token = auth.accessToken ?? '';
    const clonedRequest = addTokenHeader(requestWithLanguage, token);

    return next(clonedRequest).pipe(
        catchError(error => {
            if (
                error instanceof HttpErrorResponse &&
                !clonedRequest.url.includes('refresh') &&
                error.status === 401
            ) {
                return handle401Error(clonedRequest, next, auth, router, state);
            }

            if (error instanceof HttpErrorResponse && shouldReportApiFailure(error.status)) {
                reportApiFailureIfNeeded(trackjs, clonedRequest, router, error);
            }

            return throwError(() => error);
        }),
    );
};
